import { useMemo, useState } from 'react';

import { Badge, Card, Empty, List, Select, Skeleton, Spin, Typography, theme } from 'antd';

import ColoredFormattedNumber from 'components/ColoredFormattedNumber';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import type { TooltipProps } from 'recharts';
import type { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import DashboardSegmented from 'v2/components/Pages/Dashboard/DashboardSegmented';
import { Mask } from 'v2/components/Shared/ValuesVisibility';
import { useBestsellers } from 'v2/hooks/useBestsellers';

type DoughnutChartProps = {
  id: string;
};

type ChartSlice = {
  name: string;
  quantity: number;
  value: number;
  fill: string;
};

type Period = '7' | '30' | 'all';

enum TabKeys {
  PRODUCER = '0',
  COPRODUCER = '1',
  AFFILIATE = '2'
}

function ParticipationTooltip({ active, payload }: TooltipProps<ValueType, NameType>) {
  if (!active || !payload?.length) {
    return null;
  }

  const slice = payload[0].payload as ChartSlice & { totalQuantity: number };
  const percentage = slice.totalQuantity ? Math.round((slice.quantity / slice.totalQuantity) * 100) : 0;

  return (
    <div className='w-auto rounded-(--uizz-antd-border-radius) border border-(--uizz-antd-color-border) bg-(--uizz-antd-color-bg-container) px-3 py-2'>
      <Typography.Text strong className='wrap-break-word'>
        {slice.name}
      </Typography.Text>
      <div className='flex items-center gap-2'>
        <Badge dot color={slice.fill} />
        <Typography.Text type='secondary'>{'Participação: '}</Typography.Text>
        <Typography.Text>{`${percentage}%`}</Typography.Text>
      </div>
    </div>
  );
}

const DoughnutChart = ({ id }: DoughnutChartProps) => {
  const { token } = theme.useToken();
  const [tab, setTab] = useState<string>(TabKeys.PRODUCER);
  const [period, setPeriod] = useState<Period>('30');

  const { bestsellers, isLoading } = useBestsellers(period);

  const source = useMemo(() => {
    switch (tab) {
      case TabKeys.COPRODUCER:
        return bestsellers?.coproducer;
      case TabKeys.AFFILIATE:
        return bestsellers?.affiliate;
      default:
        return bestsellers?.producer;
    }
  }, [tab, bestsellers]);

  const labels: string[] = useMemo(() => source?.labels ?? [], [source]);
  const quantities: number[] = useMemo(() => source?.data ?? [], [source]);
  const values: number[] = useMemo(() => source?.values ?? [], [source]);
  const paletteColors = useMemo(
    () => [
      token.colorPrimary,
      token.colorPrimaryHover,
      token.colorPrimaryBorder,
      token.colorPrimaryBgHover,
      token.colorPrimaryBg
    ],
    [token]
  );

  const slices: ChartSlice[] = useMemo(() => {
    if (!labels.length) {
      return [];
    }

    return labels.map((name, index) => ({
      name,
      quantity: quantities[index] || 0,
      value: values[index] || 0,
      fill: paletteColors[index % paletteColors.length]
    }));
  }, [labels, paletteColors, quantities, values]);

  const totalPeriod = useMemo(() => values.reduce((sum, value) => sum + (value || 0), 0), [values]);
  const totalQuantity = useMemo(() => quantities.reduce((sum, quantity) => sum + quantity, 0), [quantities]);
  const chartData = useMemo(() => slices.map(slice => ({ ...slice, totalQuantity })), [slices, totalQuantity]);
  const hasData = slices.length > 0;

  return (
    <Card
      className='h-full'
      title='Conteúdos mais vendidos'
      extra={
        <div className='w-fit'>
          <Select<Period>
            className='w-full'
            size='small'
            value={period}
            onChange={setPeriod}
            options={[
              { value: '7', label: '7 dias' },
              { value: '30', label: '30 dias' },
              { value: 'all', label: 'Todos' }
            ]}
          />
        </div>
      }
    >
      <div className='h-full min-h-107.5'>
        <div>
          <Typography.Text type='secondary'>
            Total no período:{' '}
            {isLoading ? (
              <span className='inline-block w-20'>
                <Skeleton.Input active size='small' className='w-full' />
              </span>
            ) : (
              <Mask>
                <ColoredFormattedNumber value={totalPeriod} />
              </Mask>
            )}
          </Typography.Text>

          <div className='mt-4'>
            <DashboardSegmented
              value={tab}
              onChange={value => setTab(String(value))}
              disabled={isLoading}
              className='[&_.ant-segmented-item-label]:px-0'
              options={[
                { value: TabKeys.PRODUCER, label: 'Produtor' },
                { value: TabKeys.COPRODUCER, label: 'Coprodutor' },
                { value: TabKeys.AFFILIATE, label: 'Afiliado' }
              ]}
            />
          </div>

          <div>
            {isLoading ? (
              <div className='flex min-h-107.5 items-center justify-center'>
                <Spin />
              </div>
            ) : hasData ? (
              <div>
                <div id={id} className='relative h-55 pt-4'>
                  <ResponsiveContainer width='100%' height='100%'>
                    <PieChart className='**:outline-none'>
                      <Pie
                        data={chartData}
                        dataKey='quantity'
                        nameKey='name'
                        innerRadius='55%'
                        outerRadius='80%'
                        paddingAngle={2}
                        isAnimationActive={false}
                      >
                        {chartData.map(slice => (
                          <Cell key={slice.name} fill={slice.fill} />
                        ))}
                      </Pie>
                      <Tooltip content={<ParticipationTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <List
                  size='small'
                  split={false}
                  dataSource={slices}
                  renderItem={item => (
                    <List.Item>
                      <Badge
                        color={item.fill}
                        text={`${totalQuantity ? Math.round((item.quantity / totalQuantity) * 100) : 0}% ${item.name}`}
                      />
                    </List.Item>
                  )}
                />
              </div>
            ) : (
              <Empty description='Não há dados a serem mostrados' className='pt-4' />
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default DoughnutChart;
